import {
  Divider,
  Input,
  Radio,
  Select,
  Table,
  Tooltip,
  type TableProps
} from 'antd'
import { useMemo, type FC } from 'react'
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaFileCsv,
  FaPenSquare
} from 'react-icons/fa'

import { Alert, Tag } from '@/components/elements'
import { SHIPMENT_ORDER_CSV_IMPORT } from '@/constants/common/message.constants'

import { useViewportTableHeight } from '../../hooks'
import {
  buildLogiFieldOptions,
  collectUsedValues,
  EMPTY_CUSTOM_KEY_FIELD_STATE,
  resolveActiveRequiredFields,
  type ColumnMapping,
  type CsvReadConfig,
  type CustomKeyFieldState,
  type EffectiveTable,
  type LogiFieldValue,
  type SavedFormatCheck,
  type SavedFormatSaveChoice
} from '../../models'
import { FieldReferenceHelp } from '../FieldReferenceHelp'

/** 先頭データプレビューに使う行数（先頭2行から空でないセルを拾う）。 */
const DATA_PREVIEW_ROW_COUNT = 2

/**
 * 表の高さを決めるときに、表以外（モーダル枠・ステップ・案内・リファレンス・読み取り設定・
 * 必須項目チェック・表の見出し・フッター）が使う縦幅の見込み。残りの高さを全部表に回し、
 * モーダルを広げたぶんだけ表も伸びるようにする（確認画面の表と同じ決め方）。
 */
const MAPPING_TABLE_RESERVED_HEIGHT = 440
/** 保存フォーマット適用の通知（1行）を出しているときに表から差し引く縦幅。 */
const MAPPING_TEMPLATE_APPLIED_HEIGHT = 45
/** 保存フォーマットの保存し直しの案内（見出し＋選択肢）を出しているときに表から差し引く縦幅。 */
const MAPPING_SAVED_FORMAT_CHECK_HEIGHT = 90

type Props = {
  effectiveTable: EffectiveTable
  /** ステップ1の設定（リキャップ表示に使う）。 */
  readConfig: CsvReadConfig
  mapping: ColumnMapping
  /** 列index（0始まり）の紐づけ先を変更する。null=取り込まない。 */
  onChangeColumn: (columnIndex: number, value: LogiFieldValue | null) => void
  /**
   * 「保存したフォーマットを使う」で入った場合の適用フォーマット名（表示用）。
   * 保存フォーマットは初期値の供給源であり、このビュー自体は3択のどれでも同じ
   * （マッピング画面は汎用取込で常に通る）。
   */
  appliedFormatName?: string | null
  /**
   * 「列が無いので後で手入力する」とマークされた必須項目。
   * CSVに対応列が無い必須項目向けの第3の選択肢——列に紐づけずに次へ進める。
   * 永続化しない（このインポート限り）。確認画面で1件ずつ、または一括で値を入力する。
   */
  skippedFields: ReadonlySet<LogiFieldValue>
  /** 未紐づけの必須項目の「後で手入力する」マークを切り替える。 */
  onToggleSkip: (field: LogiFieldValue) => void
  /** 拠点のカスタムキー設定（使えないスロットは候補に出さず、名前は拠点の設定名にする）。 */
  customKeys?: CustomKeyFieldState
  /** 保存フォーマットで取り込むときの、定義の確かめ（それ以外は null） */
  savedFormatCheck?: SavedFormatCheck | null
}

type MappingRow = {
  key: number
  /** 実効テーブルの列index（0始まり）。 */
  columnIndex: number
}

/** 列の見出し名。ヘッダーありなら headers[i]、無ければ「列N」。 */
const columnHeaderName = (
  effectiveTable: EffectiveTable,
  columnIndex: number
): string =>
  effectiveTable.headers[columnIndex] ||
  SHIPMENT_ORDER_CSV_IMPORT.COLUMN_LABEL(columnIndex)

/** 先頭データのプレビュー文字列（先頭数行の空でないセルを「 / 」で連結）。 */
const dataPreview = (
  effectiveTable: EffectiveTable,
  columnIndex: number
): string => {
  const preview = effectiveTable.dataRows
    .slice(0, DATA_PREVIEW_ROW_COUNT)
    .map((row) => row[columnIndex] ?? '')
    .filter((v) => v !== '')
    .join(' / ')
  return preview || SHIPMENT_ORDER_CSV_IMPORT.MAPPING_DATA_EMPTY
}

/**
 * 必須項目の紐づけ進捗（紐づけ済み=チェック / 未紐づけ=感嘆符 / 手入力予定=ペン）。
 * 未紐づけ・手入力予定のタグはクリックで「後で手入力する」マークを切り替えられる
 * （紐づけ済みの項目はクリック操作の対象外）。
 *
 * 表示対象は固定必須6項目＋条件付き必須項目（運行計画の項目をいずれか1つでもマッピングすると、
 * 枠種別と配車系4項目がここに追加表示される。column-mapping.ts の resolveActiveRequiredFields 参照）。
 */
const RequiredProgress: FC<{
  usedValues: Set<LogiFieldValue>
  skippedFields: ReadonlySet<LogiFieldValue>
  onToggleSkip: (field: LogiFieldValue) => void
}> = ({ usedValues, skippedFields, onToggleSkip }) => (
  <div className="flex flex-wrap gap-1.5">
    {resolveActiveRequiredFields(usedValues).map((f) => {
      const done = usedValues.has(f.value)
      const skipped = !done && skippedFields.has(f.value)
      // 赤/緑は「エラーの有無」に見えてしまう（ここは選択済み/未選択という別軸）ため使わず、
      // グレー系の濃淡とアイコンで状態を区別する。
      const color = done ? 'secondary' : skipped ? 'gray-2' : 'gray-1'
      const icon = done ? (
        <FaCheckCircle />
      ) : skipped ? (
        <FaPenSquare />
      ) : (
        <FaExclamationCircle />
      )
      const label = skipped
        ? `${f.label}${SHIPMENT_ORDER_CSV_IMPORT.MAPPING_SKIP_SUFFIX}`
        : f.label
      const tag = (
        <Tag
          size="small"
          color={color}
          icon={icon}
          className={
            'm-0 inline-flex items-center gap-1' +
            (done ? '' : ' cursor-pointer')
          }
          onClick={done ? undefined : () => onToggleSkip(f.value)}
          data-testid={`shipment-order-csv-import__required-tag-${f.value}`}
        >
          {label}
        </Tag>
      )
      if (done) return <span key={f.value}>{tag}</span>
      return (
        <Tooltip
          key={f.value}
          title={
            skipped
              ? SHIPMENT_ORDER_CSV_IMPORT.MAPPING_SKIP_TOOLTIP_UNMARK
              : SHIPMENT_ORDER_CSV_IMPORT.MAPPING_SKIP_TOOLTIP_MARK
          }
        >
          {tag}
        </Tooltip>
      )
    })}
  </div>
)

/**
 * ステップ2: マッピング設定。
 * - アップロードCSVの各列（実効テーブルの列）を、ロジGoの配送依頼項目に紐づける。
 * - 右Selectはグループ化（必須/配送情報/運賃・費用/明細項目）。1度選んだ項目は他列の候補から消える（1項目1列）。
 * - 必須6項目の紐づけ進捗を表示。充足判定（次へ disabled）はウィザード本体（ModalContent）が持つ。
 * - 保存フォーマットを選んで入った場合は、その内容が初期値として入っている（適用フォーマット名を表示）。
 */
export const MappingView: FC<Props> = ({
  effectiveTable,
  readConfig,
  mapping,
  onChangeColumn,
  appliedFormatName = null,
  skippedFields,
  onToggleSkip,
  customKeys = EMPTY_CUSTOM_KEY_FIELD_STATE,
  savedFormatCheck = null
}) => {
  const usedValues = useMemo(() => collectUsedValues(mapping), [mapping])
  const appliedCount = usedValues.size
  const tableHeight = useViewportTableHeight(
    MAPPING_TABLE_RESERVED_HEIGHT +
      (appliedFormatName ? MAPPING_TEMPLATE_APPLIED_HEIGHT : 0) +
      (savedFormatCheck?.needsSave ? MAPPING_SAVED_FORMAT_CHECK_HEIGHT : 0)
  )

  const columns: TableProps<MappingRow>['columns'] = [
    {
      title: SHIPMENT_ORDER_CSV_IMPORT.MAPPING_COL_HEADER_CSV,
      key: 'csv',
      width: '46%',
      render: (_, row) => (
        <div>
          <div className="font-medium text-secondary">
            {columnHeaderName(effectiveTable, row.columnIndex)}
            {effectiveTable.headers.length > 0 && (
              <span className="ml-1 text-3xs text-gray-2">
                / {SHIPMENT_ORDER_CSV_IMPORT.COLUMN_LABEL(row.columnIndex)}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-2xs tabular-nums text-gray-2">
            {SHIPMENT_ORDER_CSV_IMPORT.MAPPING_DATA_PREVIEW(
              dataPreview(effectiveTable, row.columnIndex)
            )}
          </div>
        </div>
      )
    },
    {
      title: '',
      key: 'arrow',
      width: 30,
      align: 'center',
      render: () => <span className="text-gray-2">→</span>
    },
    {
      title: SHIPMENT_ORDER_CSV_IMPORT.MAPPING_COL_HEADER_LOGI,
      key: 'logi',
      render: (_, row) => {
        const current = mapping[row.columnIndex] ?? null
        return (
          <Select<LogiFieldValue>
            className="w-full"
            placeholder={SHIPMENT_ORDER_CSV_IMPORT.MAPPING_NOT_IMPORT}
            allowClear
            showSearch
            optionFilterProp="label"
            value={current ?? undefined}
            options={buildLogiFieldOptions(current, usedValues, customKeys)}
            onChange={(v) => onChangeColumn(row.columnIndex, v ?? null)}
            data-testid={`shipment-order-csv-import__column-select-${row.columnIndex}`}
          />
        )
      }
    }
  ]

  const dataSource: MappingRow[] = Array.from(
    { length: effectiveTable.columnCount },
    (_, columnIndex) => ({ key: columnIndex, columnIndex })
  )

  return (
    <div className="flex flex-col gap-3">
      <Alert title={SHIPMENT_ORDER_CSV_IMPORT.MAPPING_ALERT_MESSAGE}>
        <span className="text-xs">
          {SHIPMENT_ORDER_CSV_IMPORT.MAPPING_ALERT_DESCRIPTION}
        </span>
      </Alert>

      {/* 入力できる項目・反映先のリファレンス */}
      <div className="flex">
        <FieldReferenceHelp />
      </div>

      {/* 保存フォーマット適用の通知（初期値が埋まっただけで、この画面で確認・修正できる） */}
      {appliedFormatName && (
        <div
          className="rounded border border-table-border bg-geek-blue-1 px-3 py-2 text-xs text-secondary"
          data-testid="shipment-order-csv-import__template-applied"
        >
          {SHIPMENT_ORDER_CSV_IMPORT.MAPPING_TEMPLATE_APPLIED(
            appliedFormatName,
            appliedCount
          )}
        </div>
      )}

      {/* 保存フォーマットは定義として扱う。CSV に無い列があるか、対応づけを変えたら、保存し直しを選ぶまで進めない */}
      {savedFormatCheck?.needsSave && (
        <Alert
          color="red"
          title={
            savedFormatCheck.missingColumns.length > 0
              ? SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_MISSING_COLUMNS(
                  savedFormatCheck.missingColumns.join('、')
                )
              : SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_EDITED
          }
          data-testid="shipment-order-csv-import__saved-format-check"
        >
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <Radio.Group
              value={savedFormatCheck.choice}
              onChange={(e) =>
                savedFormatCheck.onChangeChoice(
                  e.target.value as SavedFormatSaveChoice
                )
              }
              data-testid="shipment-order-csv-import__saved-format-save-choice"
            >
              <Radio value="update">
                <span className="text-xs">
                  {SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_SAVE_UPDATE(
                    savedFormatCheck.formatName
                  )}
                </span>
              </Radio>
              <Radio value="new">
                <span className="text-xs">
                  {SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_SAVE_NEW}
                </span>
              </Radio>
            </Radio.Group>
            {savedFormatCheck.choice === 'new' && (
              <span className="flex flex-col gap-0.5">
                <Input
                  size="small"
                  className="w-72"
                  status={
                    savedFormatCheck.isNewNameDuplicated ? 'error' : undefined
                  }
                  placeholder={
                    SHIPMENT_ORDER_CSV_IMPORT.SAVE_FORMAT_NAME_PLACEHOLDER
                  }
                  value={savedFormatCheck.newName}
                  onChange={(e) =>
                    savedFormatCheck.onChangeNewName(e.target.value)
                  }
                  data-testid="shipment-order-csv-import__saved-format-new-name"
                />
                {savedFormatCheck.isNewNameDuplicated && (
                  <span className="text-2xs text-red">
                    {SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_NAME_DUPLICATED}
                  </span>
                )}
              </span>
            )}
          </div>
        </Alert>
      )}

      {/* ステップ1で指定した内容（読み取り専用リキャップ） */}
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-secondary">
        <FaFileCsv className="text-green" />
        <span>
          {readConfig.hasHeader
            ? SHIPMENT_ORDER_CSV_IMPORT.MAPPING_RECAP_HEADER(
                readConfig.headerRowNo
              )
            : SHIPMENT_ORDER_CSV_IMPORT.MAPPING_RECAP_NO_HEADER}
        </span>
        <Divider type="vertical" />
        <span>
          {SHIPMENT_ORDER_CSV_IMPORT.MAPPING_RECAP_START_ROW(
            readConfig.startRowNo
          )}
        </span>
      </div>

      {/* 必須項目チェック欄: 必須項目が列に紐づいているか（or 後で手入力予定か）の状態確認。
          未紐づけタグをクリックすると「後で手入力する」に切り替えられる。
          ラベルは静的な必須項目一覧でなく「選択されているかのチェック」だと伝わる文言にする */}
      <div className="flex flex-col gap-1.5 rounded border border-table-border bg-neutral-3 p-2.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="whitespace-nowrap text-xs font-medium text-secondary">
            {SHIPMENT_ORDER_CSV_IMPORT.MAPPING_REQUIRED_LABEL}
          </span>
          <RequiredProgress
            usedValues={usedValues}
            skippedFields={skippedFields}
            onToggleSkip={onToggleSkip}
          />
        </div>
        <span className="text-2xs text-gray-2">
          {SHIPMENT_ORDER_CSV_IMPORT.MAPPING_SKIP_HINT}
        </span>
      </div>

      <Table<MappingRow>
        size="small"
        bordered
        rowKey="key"
        pagination={false}
        columns={columns}
        dataSource={dataSource}
        scroll={{ y: tableHeight }}
      />
    </div>
  )
}
